import { Box, CircularProgress, Switch, Typography } from '@mui/material';
import { pendingKeyFor } from '../useProductAccessActions';
import { displayNameFor, secondaryLineFor } from '../userIdentity';

const primarySx = {
  fontFamily: 'Rubik',
  fontWeight: 600,
  fontSize: { xs: '10px', md: '11px', lg: '13px', xl: '19px' },
  color: '#08231B',
};

const metaSx = {
  fontFamily: 'Rubik',
  fontSize: { xs: '10px', lg: '11px', xl: '14px' },
  color: '#8A909C',
};

const productLabelSx = {
  fontFamily: 'Rubik',
  fontWeight: 600,
  fontSize: { xs: '10px', md: '11px', lg: '13px', xl: '19px' },
  color: '#000000',
};

/**
 * From `sm` up the products become columns with one header each, so the tracks
 * have to be built from the catalog rather than hardcoded — a third product
 * widens the grid instead of needing a layout change.
 *
 * Below `sm` the row stacks and this is unused: the product name is repeated
 * inside each row because there is no header to carry it.
 */
export const gridColumnsFor = (count) => ({
  sm: `1.4fr repeat(${count}, minmax(84px, 0.7fr))`,
  md: `1.8fr repeat(${count}, minmax(104px, 0.8fr))`,
  xl: `2fr repeat(${count}, minmax(132px, 0.8fr))`,
});

/**
 * The four states a product can be in, from the two booleans the API returns,
 * split into the word beside the switch and the gate note under it.
 * `org_enabled` is displayed, never directly operable — the switch always drives
 * `user_access`, and granting through a closed gate is confirmed in a dialog.
 */
export const statusTextFor = ({ org_enabled: orgEnabled, user_access: userAccess }) => {
  if (orgEnabled && userAccess) return 'On';
  if (orgEnabled) return 'Off';
  // The gate is shut, so the product does not open either way; `Blocked` marks
  // the half-state, where the user row exists and is doing nothing.
  return userAccess ? 'Blocked' : 'Off';
};

// Shown under the switch only when the organisation gate is what is in the way.
export const gateNoteFor = ({ org_enabled: orgEnabled }) =>
  orgEnabled ? null : 'Not enabled for organization';

// The switch reads off whenever the product does not actually open, so the
// half-state (gate off, user row present) shows off with a repair affordance.
export const switchCheckedFor = ({ org_enabled: orgEnabled, user_access: userAccess }) =>
  Boolean(orgEnabled && userAccess);

const UserAccessRow = ({ user, pendingKey, onToggle }) => {
  const products = Array.isArray(user?.products) ? user.products : [];

  return (
    <Box
      data-testid={`product-access-row-${user?.id}`}
      sx={{
        display: { xs: 'flex', sm: 'grid' },
        flexDirection: 'column',
        gridTemplateColumns: gridColumnsFor(products.length),
        alignItems: { xs: 'stretch', sm: 'center' },
        columnGap: { sm: 1, xl: 2 },
        rowGap: { xs: 1.5, sm: 0 },
        py: { xs: 1.5, sm: 1.5, xl: 2 },
        borderBottom: '2px solid #EBEBEB',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      {/* Identity — verification is a caveat on the same line, not a badge.
          Both lines go through userIdentity so a null name or a nameless
          organisation still reads as something (PA-03, PA-07). */}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={primarySx}>{displayNameFor(user)}</Typography>
        <Typography sx={metaSx}>{secondaryLineFor(user)}</Typography>
      </Box>

      {/* `contents` from sm up so each product lands in its own header column;
          below that this is the stacked list under the identity block. */}
      <Box sx={{ display: { xs: 'flex', sm: 'contents' }, flexDirection: 'column', gap: 1 }}>
        {products.map((product) => {
          const busy = pendingKey === pendingKeyFor(user?.id, product.key);
          const gateNote = gateNoteFor(product);
          return (
            <Box
              key={product.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'space-between', sm: 'flex-start' },
                gap: { xs: 1, sm: 0 },
                minWidth: 0,
              }}
            >
              {/* The column header carries the name from sm up. */}
              <Typography sx={{ ...productLabelSx, display: { xs: 'block', sm: 'none' } }}>
                {product.label}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'flex-end', sm: 'flex-start' },
                  minWidth: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={metaSx} data-testid={`product-status-${user?.id}-${product.key}`}>
                    {statusTextFor(product)}
                  </Typography>
                  <Switch
                    size="small"
                    disabled={busy}
                    checked={switchCheckedFor(product)}
                    onChange={(e) => onToggle(user, product, e.target.checked)}
                    inputProps={{ 'aria-label': `${product.label} access for ${displayNameFor(user)}` }}
                  />
                  {busy && <CircularProgress size={16} />}
                </Box>
                {gateNote && (
                  <Typography
                    sx={{ ...metaSx, mt: -0.25 }}
                    data-testid={`product-gate-note-${user?.id}-${product.key}`}
                  >
                    {gateNote}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default UserAccessRow;
